import { Outlet, Link, useLocation, useNavigate } from 'react-router';
import { 
  LayoutDashboard, Building, Grid3x3, Wrench, MapPin, Menu, X, LogOut, Settings, Database, FileText
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { useSettings } from '../../contexts/SettingsContext';
import AdminLogin from './AdminLogin';

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { settings } = useSettings();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('admin_authenticated') === 'true';
  });

  // Debug: Log current path
  console.log('Current admin path:', location.pathname);

  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  const navigation = [
    { name: 'لوحة التحكم', href: '/admin', icon: LayoutDashboard },
    { name: 'العقارات', href: '/admin/properties', icon: Building },
    { name: 'التصنيفات', href: '/admin/categories', icon: Grid3x3 },
    { name: 'المرافق', href: '/admin/facilities', icon: Wrench },
    { name: 'الأماكن القريبة', href: '/admin/nearby', icon: MapPin },
    { name: 'من نحن', href: '/admin/about', icon: FileText },
    { name: 'محرر SQL', href: '/admin/sql-editor', icon: Database },
    { name: 'إعدادات الموقع', href: '/admin/settings', icon: Settings },
  ];

  const isActive = (path: string) => {
    // Exact match for dashboard
    if (path === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/';
    }
    // For other routes, check if pathname starts with the path
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <header className="bg-white border-b shadow-sm fixed top-0 left-0 right-0 z-20">
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-gray-600 hover:text-gray-800"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            <Link to="/" className="flex items-center gap-2">
              {settings.logo ? (
                <img src={settings.logo} alt={settings.siteNameArabic} className="h-8 object-contain" />
              ) : (
                <div className="text-[#8B7109] text-xl font-bold">
                  <span className="text-[#D4AF37]">دار</span> العقار
                </div>
              )}
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 rounded-md gap-1.5 px-3"
            >
              عرض الموقع
            </a>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                localStorage.removeItem('admin_authenticated');
                setIsAuthenticated(false);
                navigate('/');
              }}
            >
              <LogOut className="w-4 h-4 ml-2" />
              تسجيل الخروج
            </Button>
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-16 right-0 h-[calc(100vh-4rem)] w-64 bg-white border-l shadow-lg z-10
          transition-transform duration-300 lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        `}>
          <nav className="p-4 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              // Debug: Log active state
              console.log(`Navigation item: ${item.name}, href: ${item.href}, active: ${active}`);
              
              return (
                <button
                  key={item.href}
                  onClick={() => {
                    console.log(`Navigating to: ${item.href}`);
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors w-full text-right
                    ${active 
                      ? 'bg-[#D4AF37] text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8">
          {/* Debug: Show current path */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-bold text-blue-800 mb-2">Debug Info:</h3>
            <p className="text-sm text-blue-600">Current Path: {location.pathname}</p>
            <p className="text-sm text-blue-600">Current URL: {window.location.href}</p>
          </div>
          <Outlet />
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-0 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
}